import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { TanstackProvider } from "fumadocs-core/framework/tanstack";
import type { PageTree } from "fumadocs-core/server";
import { createClientLoader } from "fumadocs-mdx/runtime/vite";
import { DocsLayout } from "fumadocs-ui/layouts/docs";
import defaultMdxComponents from "fumadocs-ui/mdx";
import {
	DocsBody,
	DocsDescription,
	DocsPage,
	DocsTitle,
} from "fumadocs-ui/page";
import { RootProvider } from "fumadocs-ui/provider/base";
import { useMemo } from "react";
import { source } from "@/lib/source";
import { docs } from "../../source.generated";

export const Route = createFileRoute("/docs/$")({
	component: Page,
	loader: async ({ params }) => {
		const data = await loader({ data: params._splat?.split("/") ?? [] });
		await docsClientLoader.preload(data.path);
		return data;
	},
});

// a wrapper because we don't want `loader` to be called on client-side
const loader = createServerFn({
	method: "GET",
})
	.inputValidator((slugs: string[]) => slugs)
	.handler(async ({ data: slugs }) => {
		const page = source.getPage(slugs);
		if (!page) throw notFound();

		return {
			tree: source.pageTree as object,
			path: page.path,
		};
	});

export const docsClientLoader = createClientLoader(docs.doc, {
	id: "docs",
	component({ toc, frontmatter, default: MDX }) {
		return (
			<DocsPage toc={toc}>
				<DocsTitle>{frontmatter.title}</DocsTitle>
				<DocsDescription>{frontmatter.description}</DocsDescription>
				<DocsBody>
					<MDX
						components={{
							...defaultMdxComponents,
						}}
					/>
				</DocsBody>
			</DocsPage>
		);
	},
});

export function DocsContent({
	path,
	tree,
}: {
	path: string;
	tree: PageTree.Folder;
}) {
	const Content = docsClientLoader.getComponent(path);
	const resolvedTree = useMemo(() => transformPageTree(tree), [tree]);

	return (
		<TanstackProvider>
			<RootProvider theme={{ enabled: false }}>
				<div className="docs-theme font-sans">
					<DocsLayout
						tree={resolvedTree}
						containerProps={{
							className: "font-sans",
						}}
						nav={{
							title: "Fumadocs Tanstack",
						}}
					>
						<Content />
					</DocsLayout>
				</div>
			</RootProvider>
		</TanstackProvider>
	);
}

function Page() {
	const data = Route.useLoaderData();

	return <DocsContent path={data.path} tree={data.tree as PageTree.Folder} />;
}

function transformPageTree(tree: PageTree.Folder): PageTree.Folder {
	function page(item: PageTree.Item) {
		if (typeof item.icon !== "string") return item;

		return {
			...item,
			icon: (
				<span
					dangerouslySetInnerHTML={{
						__html: item.icon,
					}}
				/>
			),
		};
	}

	return {
		...tree,
		index: tree.index ? page(tree.index) : undefined,
		children: tree.children.map((item) => {
			if (item.type === "page") return page(item);
			if (item.type === "folder") return transformPageTree(item);
			return item;
		}),
	};
}
